import React, { useState } from 'react';
import { useRecoilValue } from 'recoil';
import { Card } from '../../components/Card';
import { CardTitle } from '../../components/CardTitle';
import { CollapsePanelMobile } from '../../components/CollapsePanelMobile';
import { useSupportModal } from '../../hooks/actions/useSupportModal';
import { environmentContextState } from '../../recoil/atoms';
import { Button } from '../../root/components/buttons/Button';
import { LeadGenerationForm } from '../../root/components/LeadGenerationForm';
import { Link } from '../../root/components/Link';
import { assetPathFn } from '../../util';
import styles from './MainFooterMobile.module.scss';

type FooterPanelSections = 'contact' | 'returns' | 'gift-cards' | 'affiliates' | 'resources';

// eslint-disable-next-line max-lines-per-function
export const MainFooterMobile: React.FC = () => {
  const environmentContext = useRecoilValue(environmentContextState);
  const launchSupportModal = useSupportModal();
  const [activeFooterSection, setActiveFooterSection] = useState<FooterPanelSections>();
  const toggleActiveFooterSection = (key: FooterPanelSections) => {
    setActiveFooterSection(activeFooterSection === key ? undefined : key);
  };

  return (
    <>
      <Card>
        <footer role="contentinfo" className={styles.footer}>
          <CardTitle text={'Join Our Community'} />
          <div className={styles.footerContentsWrapper}>
            {/* SIGN UP SECTION*/}
            <div>Get exclusive offers and product previews</div>
            <div className={styles.klaviyoInputArea}>
              <LeadGenerationForm placeholder="Enter your email" klaviyoFormKeys={environmentContext.klaviyo.newsletterFooter} buttonMessage={'Subscribe'} />
            </div>

            {/* JOIN THE HIVE SECTION */}
            <div className={styles.section}>Join the Hive and start collecting loyalty points</div>
            <Link
              buttonType="secondary"
              size="small"
              text="Loyalty Program"
              href="/pages/loyalty"
              dataTrackLabel="loyalty-program"
              dataTrackLocation="footer"
            />
            <Link
              buttonType="secondary"
              size="small"
              text="Referral Program"
              dataTrackLabel="referral-program"
              dataTrackLocation="footer"
              href="https://honeylove.referralcandy.com/"
            />

            {/* FOLLOW US SECTION */}
            <div className={styles.section}>Follow us</div>
            <div className={styles.socialIcons}>
              <Link dataTrackLabel="Facebook Icon" dataTrackLocation="footer" href="https://www.facebook.com/GetHoneyLove/">
                <img src={assetPathFn('facebook.png')} alt="honeylove facebook" />
              </Link>
              <Link dataTrackLabel="Instagram Icon" dataTrackLocation="footer" href="https://www.instagram.com/honeylove/">
                <img src={assetPathFn('instagram.png')} alt="honeylove instagram" />
              </Link>
              <Link dataTrackLabel="Twitter Icon" dataTrackLocation="footer" href="https://twitter.com/gethoneylove">
                <img src={assetPathFn('twitter-icon.png')} alt="honeylove twitter" />
              </Link>
              <Link dataTrackLabel="Pinterest Icon" dataTrackLocation="footer" href="https://www.pinterest.com/gethoneylove/">
                <img src={assetPathFn('pinterest.png')} alt="honeylove pinterest" />
              </Link>
              <Link dataTrackLabel="YouTube Icon" dataTrackLocation="footer" href="https://www.youtube.com/channel/UCVE8b1s_zmMUBIDbULsIqlg/videos">
                <img src={assetPathFn('youtube.png')} alt="honeylove youtube" />
              </Link>
            </div>

            {/* OTHER LINKS SECTION */}
            <div className={styles.section} />
            <CollapsePanelMobile
              type="external-state"
              title={'Contact Us'}
              setIsActive={() => toggleActiveFooterSection('contact')}
              isActive={activeFooterSection === 'contact'}
            >
              <div className={styles.footerPanelSection}>
                <div> 6am to 6pm PST every day</div>
                <Button size="small" text="Contact Us" onClick={launchSupportModal} />
              </div>
            </CollapsePanelMobile>
            <CollapsePanelMobile
              type="external-state"
              title={'Easy Returns & FAQs'}
              setIsActive={() => toggleActiveFooterSection('returns')}
              isActive={activeFooterSection === 'returns'}
            >
              <div className={styles.footerPanelSection}>
                <div>Enjoy free US shipping and 30-day returns.</div>
                <Link buttonType="secondary" size="small" text="Returns" dataTrackLabel="Returns" dataTrackLocation="footer" href="/pages/returns" />
                <Link buttonType="secondary" size="small" text="FAQs" dataTrackLabel="FAQ" dataTrackLocation="footer" href="https://help.honeylove.com" />
              </div>
            </CollapsePanelMobile>
            <CollapsePanelMobile
              type="external-state"
              title={'Gift Cards & Sale'}
              setIsActive={() => toggleActiveFooterSection('gift-cards')}
              isActive={activeFooterSection === 'gift-cards'}
            >
              <div className={styles.footerPanelSection}>
                <Link
                  buttonType="secondary"
                  size="small"
                  text="Gift Cards"
                  dataTrackLabel="e-gift-card"
                  dataTrackLocation="footer"
                  href="/products/e-gift-card"
                />
                <Link
                  buttonType="secondary"
                  size="small"
                  text="Sale"
                  dataTrackLabel="Discount products"
                  dataTrackLocation="footer"
                  href="/collections/discounted"
                />
              </div>
            </CollapsePanelMobile>
            <CollapsePanelMobile
              type="external-state"
              title={'Resources'}
              setIsActive={() => toggleActiveFooterSection('resources')}
              isActive={activeFooterSection === 'resources'}
            >
              <div className={styles.footerPanelSection}>
                <Link
                  buttonType="secondary"
                  size="small"
                  text="How to wear"
                  dataTrackLabel="How to wear"
                  dataTrackLocation="footer"
                  href="/pages/how-to-wear"
                />
                <Link
                  buttonType="secondary"
                  size="small"
                  text="Fit consultation"
                  dataTrackLabel="Fit consultation"
                  dataTrackLocation="footer"
                  href="/pages/consultation"
                />
                <Link buttonType="secondary" size="small" text="Blog" dataTrackLabel="Blog" dataTrackLocation="footer" href="/blogs/essays" />
                <Link buttonType="secondary" size="small" text="Affiliates" dataTrackLabel="Affiliates" dataTrackLocation="footer" href="/pages/affiliates" />
                <Link buttonType="secondary" size="small" text="Reviews" dataTrackLabel="Reviews" dataTrackLocation="footer" href="/pages/reviews" />
                <Link
                  buttonType="secondary"
                  size="small"
                  text="Frontline worker"
                  subText="discount"
                  dataTrackLabel="Frontline worker discount"
                  dataTrackLocation="footer"
                  href="/pages/frontline-workers"
                />
                <Link buttonType="secondary" size="small" text="Coupon Policy" dataTrackLabel="Coupons" dataTrackLocation="footer" href="/pages/coupons" />
                <Link buttonType="secondary" size="small" text="Amazon Policy" dataTrackLabel="Amazon" dataTrackLocation="footer" href="/pages/amazon" />
                <Link
                  buttonType="secondary"
                  size="small"
                  text="Sustainability"
                  dataTrackLabel="Sustainability"
                  dataTrackLocation="footer"
                  href="/pages/sustainability"
                />
              </div>
            </CollapsePanelMobile>
            <div className={styles.termsAndPolicySection}>
              <div>
                <Link dataTrackLabel="Terms of Service" dataTrackLocation="footer" href="/pages/terms-of-service">
                  Terms of service
                </Link>
                {' • '}
                <Link dataTrackLabel="Privacy Policy" dataTrackLocation="footer" href="/pages/privacy-policy">
                  Privacy policy
                </Link>
              </div>
              <div>
                <Button
                  dataTrackLabel="Cookie Settings/CCPA"
                  dataTrackLocation="footer"
                  buttonType="link"
                  size="small"
                  onClick={() => document.getElementById('ot-sdk-btn')?.click()}
                  text="Cookie Settings/CCPA"
                />
                {' • '}
                <Link dataTrackLabel="Accessibility" dataTrackLocation="footer" href="/pages/accessibility">
                  Accessibility Statement
                </Link>
              </div>
              <div className={styles.accessibility}>
                <Link
                  dataTrackLabel="Icon & Assistive Technology"
                  dataTrackLocation="footer"
                  href="https://www.essentialaccessibility.com/honeylove?utm_source=honeylovehomepage&utm_medium=iconlarge&utm_term=eachannelpage&utm_content=header&utm_campaign=honeylove"
                >
                  <img
                    className={styles.accessibilityIcon}
                    src={assetPathFn('eA_Icon.svg')}
                    alt="This icon serves as a link to download the eSSENTIAL Accessibility assistive technology app for individuals with physical disabilities. It is featured as part of our commitment to diversity and inclusion."
                  />
                </Link>
              </div>
            </div>
          </div>
        </footer>
      </Card>
      <div className={styles.copyright}>Copyright 2022 Honeylove®</div>
    </>
  );
};

export default MainFooterMobile;
